// The order and groups of a session list, with nothing else imported: the machine ranks the
// sessions (Session.rank) and keys their groups (Session.row); here they're only sorted and
// bucketed. e2e/tests/organize.spec.ts checks it against the machine's shared fixture
// (internal/agent/testdata/rows.json), as the CLI's and the Mac's tests do.

import type { Session } from '../net/protocol';

export type Grouping = 'none' | 'folder' | 'program';

export interface SessionGroup {
  key: string;
  /** "~/Projects/cmx · main", "Claude Code", "Terminal"; '' when not grouped. */
  label: string;
  /** By program: the program its header's glyph shows. */
  program?: string;
  sessions: Session[];
}

/** In the machine's order (rank; a machine from before ranks: the order they were started). */
export const ranked = (sessions: Session[]) => [...sessions].sort((a, b) => (a.rank ?? a.id) - (b.rank ?? b.id));

/** In the order the person arranged (`order`: ids, first to last); one that isn't there (started since) goes after
 *  them, in the order they were started. Nothing arranged: the order they were started. */
export function arranged<T extends { id: number }>(items: T[], order: readonly number[]): T[] {
  const at = new Map(order.map((id, i) => [id, i]));
  return [...items].sort((a, b) => {
    const x = at.get(a.id);
    const y = at.get(b.id);
    return x !== undefined && y !== undefined ? x - y : x !== undefined ? -1 : y !== undefined ? 1 : a.id - b.id;
  });
}

/** `all` (every id, in order) with `ids` taken out and put back together before `before` (null, or not there: last). */
export function moved(all: number[], ids: number[], before: number | null): number[] {
  const rest = all.filter((id) => !ids.includes(id));
  const i = before === null ? -1 : rest.indexOf(before);
  return i < 0 ? [...rest, ...ids] : [...rest.slice(0, i), ...ids, ...rest.slice(i)];
}

/** Sessions (already in list order) in groups by the machine's keys, each group where its first session is. */
export function groupSessions(sessions: Session[], m: Grouping): SessionGroup[] {
  if (m === 'none') return [{ key: '', label: '', sessions }];
  const groups: SessionGroup[] = [];
  const at = new Map<string, SessionGroup>();
  for (const s of sessions) {
    const g = s.row?.groups[m] ?? { key: '', label: '' };
    let group = at.get(g.key);
    if (!group) {
      group = { key: g.key, label: g.label, program: m === 'program' && g.key ? g.key : undefined, sessions: [] };
      at.set(g.key, group);
      groups.push(group);
    }
    group.sessions.push(s);
  }
  return groups;
}

